import {
  MoldAssessmentAnswers,
  MoldAssessmentResult,
  MoldCategory,
  RecommendationLevel,
  ConcerningSymptom,
  EnvironmentalIndicator,
} from '@/types/mold-assessment';
import {
  MOLD_ASSESSMENT_SECTIONS,
  getQuestionById,
} from '@/data/mold-assessment-data';

interface CategoryScore {
  score: number;
  maxScore: number;
  percentage: number;
}

/**
 * Calculate likelihood percentage based on symptom count
 * This is the PRIMARY driver of the score - more symptoms = higher likelihood
 */
function calculateLikelihoodFromSymptomCount(symptomCount: number): number {
  // Based on realistic expectations:
  // - Healthy person: 0-5 symptoms
  // - Possible issue: 6-10 symptoms
  // - Likely issue: 11-15 symptoms
  // - Very likely: 16+ symptoms

  if (symptomCount === 0) return 5;
  if (symptomCount <= 3) return 15;
  if (symptomCount <= 5) return 25;
  if (symptomCount <= 7) return 40;
  if (symptomCount <= 10) return 55;
  if (symptomCount <= 13) return 68;
  if (symptomCount <= 16) return 78;
  if (symptomCount <= 20) return 85;
  if (symptomCount <= 25) return 91;
  if (symptomCount <= 30) return 95;
  return 98; // 31+ symptoms
}

/**
 * Calculate severity modifier based on average severity of checked symptoms
 * High severity symptoms indicate more mold-specific issues
 */
function calculateSeverityModifier(totalScore: number, symptomCount: number): number {
  if (symptomCount === 0) return 0;

  const averageSeverity = totalScore / symptomCount;

  // Average severity ranges from 1.0 to 3.0
  // If heavily weighted toward severe symptoms, add a boost
  if (averageSeverity >= 2.5) return 5;
  if (averageSeverity >= 2.2) return 3;
  if (averageSeverity >= 2.0) return 1;
  return 0;
}

/**
 * Calculate environmental factor boost
 * If they have environmental indicators, that's a strong signal
 */
function calculateEnvironmentalBoost(answers: MoldAssessmentAnswers): number {
  const environmentalQuestions = [
    'env-1', // Water damage
    'env-2', // Visible mold
    'env-3', // Musty smell
    'env-7', // Symptoms improve away from home
    'env-8', // Symptoms worse in certain rooms
    'env-9', // Family members have similar symptoms
  ];

  const checkedEnvFactors = environmentalQuestions.filter(id => answers[id] === true).length;

  if (checkedEnvFactors >= 3) return 5;
  if (checkedEnvFactors >= 2) return 3;
  if (checkedEnvFactors >= 1) return 1;
  return 0;
}

/**
 * Get environmental indicators that were checked
 * Key indicators are those that strongly suggest mold exposure
 */
function getEnvironmentalIndicators(answers: MoldAssessmentAnswers): EnvironmentalIndicator[] {
  // Map of env question IDs to their display text and whether they're key indicators
  const envQuestionMap: { [id: string]: { text: string; isKeyIndicator: boolean } } = {
    'env-1': { text: 'Water damage in your home (past or present)', isKeyIndicator: true },
    'env-2': { text: 'Visible mold growth anywhere in your home', isKeyIndicator: true },
    'env-3': { text: 'Musty or moldy smell in your home', isKeyIndicator: true },
    'env-4': { text: 'Leaky roof, pipes, or windows', isKeyIndicator: false },
    'env-5': { text: 'Flooding history in basement or crawl space', isKeyIndicator: true },
    'env-6': { text: 'High humidity in your home', isKeyIndicator: false },
    'env-7': { text: 'Symptoms improve when away from home', isKeyIndicator: true },
    'env-8': { text: 'Symptoms worse in certain rooms', isKeyIndicator: true },
    'env-9': { text: 'Other family members or pets have similar symptoms', isKeyIndicator: true },
    'env-10': { text: 'Live in a humid climate or near water', isKeyIndicator: false },
  };

  const indicators: EnvironmentalIndicator[] = [];

  Object.entries(envQuestionMap).forEach(([id, info]) => {
    if (answers[id] === true) {
      indicators.push({
        id,
        text: info.text,
        isKeyIndicator: info.isKeyIndicator,
      });
    }
  });

  // Sort so key indicators appear first
  return indicators.sort((a, b) => {
    if (a.isKeyIndicator && !b.isKeyIndicator) return -1;
    if (!a.isKeyIndicator && b.isKeyIndicator) return 1;
    return 0;
  });
}

/**
 * Get concerning symptoms (severity 3) grouped by category
 */
function getConcerningSymptoms(answers: MoldAssessmentAnswers): ConcerningSymptom[] {
  const concerningByCategory: { [key: string]: { displayName: string; symptoms: string[] } } = {};

  MOLD_ASSESSMENT_SECTIONS.forEach(section => {
    section.questions.forEach(question => {
      if (answers[question.id] === true && question.severity === 3) {
        if (!concerningByCategory[section.category]) {
          concerningByCategory[section.category] = {
            displayName: section.title,
            symptoms: [],
          };
        }
        concerningByCategory[section.category].symptoms.push(question.text);
      }
    });
  });

  // Convert to array and filter out empty categories
  return Object.entries(concerningByCategory)
    .filter(([_, data]) => data.symptoms.length > 0)
    .map(([category, data]) => ({
      category,
      categoryDisplayName: data.displayName,
      symptoms: data.symptoms,
    }));
}

/**
 * Calculate the mold assessment score based on answers
 * Uses SYMPTOM COUNT as the primary driver, with severity and environmental modifiers
 */
export function calculateMoldAssessmentScore(
  answers: MoldAssessmentAnswers
): MoldAssessmentResult {
  let totalScore = 0;
  let maxPossibleScore = 0;
  let symptomCount = 0;
  let totalQuestions = 0;
  const categoryScores: { [key in MoldCategory]?: CategoryScore } = {};

  // Initialize category scores
  MOLD_ASSESSMENT_SECTIONS.forEach(section => {
    categoryScores[section.category] = {
      score: 0,
      maxScore: 0,
      percentage: 0,
    };
  });

  // Calculate scores
  MOLD_ASSESSMENT_SECTIONS.forEach(section => {
    section.questions.forEach(question => {
      const answer = answers[question.id];
      const severity = question.severity;

      totalQuestions++;

      // Add to max possible score
      maxPossibleScore += severity;
      if (categoryScores[section.category]) {
        categoryScores[section.category]!.maxScore += severity;
      }

      // If answer is true (checkbox checked), add severity to score
      if (answer === true) {
        totalScore += severity;
        symptomCount++;
        if (categoryScores[section.category]) {
          categoryScores[section.category]!.score += severity;
        }
      }
    });
  });

  // Calculate percentages for each category
  Object.keys(categoryScores).forEach(key => {
    const category = key as MoldCategory;
    const catScore = categoryScores[category];
    if (catScore && catScore.maxScore > 0) {
      catScore.percentage = Math.round((catScore.score / catScore.maxScore) * 100);
    }
  });

  // NEW SCORING: Symptom-count driven with modifiers
  let baseLikelihood = calculateLikelihoodFromSymptomCount(symptomCount);
  const severityModifier = calculateSeverityModifier(totalScore, symptomCount);
  const environmentalBoost = calculateEnvironmentalBoost(answers);

  // Final percentage (capped at 98%)
  const percentage = Math.min(98, baseLikelihood + severityModifier + environmentalBoost);

  // Get concerning symptoms for callout
  const concerningSymptoms = getConcerningSymptoms(answers);

  // Get environmental indicators that were checked
  const environmentalIndicators = getEnvironmentalIndicators(answers);

  // Determine recommendation level based on new thresholds
  const recommendation = getRecommendationLevel(percentage);

  return {
    score: totalScore,
    maxPossibleScore,
    percentage,
    symptomCount,
    totalQuestions,
    recommendation,
    concerningSymptoms,
    environmentalIndicators,
    categoryScores,
  };
}

/**
 * Get recommendation level based on likelihood percentage
 * 70%+ = high (High Likelihood - strongly recommend action)
 * 40-69% = moderate (Moderate Possibility - worth investigating)
 * <40% = low (Lower Likelihood - but don't dismiss concerns)
 */
export function getRecommendationLevel(percentage: number): RecommendationLevel {
  if (percentage >= 70) {
    return 'high';
  } else if (percentage >= 40) {
    return 'moderate';
  }
  return 'low';
}

/**
 * Get recommendation details for display
 */
export function getRecommendationDetails(level: RecommendationLevel): {
  title: string;
  description: string;
  color: string;
  bgColor: string;
  borderColor: string;
} {
  switch (level) {
    case 'high':
      return {
        title: 'High Likelihood',
        description: 'Your symptom profile strongly indicates mold may be affecting your health. We strongly recommend professional mold inspection and health evaluation.',
        color: 'text-red-700',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
      };
    case 'moderate':
      return {
        title: 'Moderate Likelihood',
        description: 'Your symptoms suggest mold exposure is a real possibility worth investigating. Consider a professional mold inspection to rule out or confirm exposure.',
        color: 'text-yellow-700',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
      };
    case 'low':
      return {
        title: 'Lower Likelihood',
        description: 'Based on your responses, mold is less likely to be the primary cause, but if you have environmental concerns, evaluation may still be worthwhile.',
        color: 'text-green-700',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
      };
  }
}

/**
 * Get category display name
 */
export function getCategoryDisplayName(category: MoldCategory): string {
  const names: { [key in MoldCategory]: string } = {
    respiratory: 'Respiratory & Sinus',
    brain: 'Brain & Cognition',
    neuro_pain: 'Neurological - Pain & Sensation',
    neuro_movement: 'Neurological - Movement',
    musculoskeletal: 'Musculoskeletal Pain',
    energy: 'Energy & Sleep',
    skin: 'Skin, Hair & Nails',
    digestive: 'Digestive System',
    hormonal: 'Hormonal & Metabolic',
    sensory: 'Sensory Sensitivities',
    mood: 'Mood & Mental Health',
    heart: 'Heart & Circulation',
    environmental: 'Environmental Factors',
    medical_journey: 'Medical Journey',
  };
  return names[category];
}

/**
 * Get high severity symptoms that were checked
 */
export function getHighSeveritySymptoms(
  answers: MoldAssessmentAnswers
): string[] {
  const highSeverity: string[] = [];

  Object.entries(answers).forEach(([questionId, answer]) => {
    if (answer === true) {
      const question = getQuestionById(questionId);
      if (question && question.severity === 3) {
        highSeverity.push(question.text);
      }
    }
  });

  return highSeverity;
}

/**
 * Count total symptoms checked
 */
export function countCheckedSymptoms(answers: MoldAssessmentAnswers): number {
  return Object.values(answers).filter(a => a === true).length;
}
